import { UsersService } from '@klangio/ai2notes-ts-client';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ReportProblem } from '@mui/icons-material';
import { Box } from '@mui/system';
import type { WithUser } from '../../../../context';
import { withUserContext } from '../../../../context';
import { AUTH_URL } from '../../../../utils';

export type DeleteUserAccountDialogProps = {
  open: boolean;
  onClose: () => void;
} & WithUser;

const FailedContent: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box display="flex" alignItems="center" flexDirection="column">
      <ReportProblem color="error" fontSize="large" />
      <Typography align="center" variant="h5" marginBottom={1}>
        <strong>{t('dialog.user_delete.failed_title')}</strong>
      </Typography>
      <Typography align="center" variant="body1" marginBottom={1}>
        {t('dialog.user_delete.failed_text')}
      </Typography>
    </Box>
  );
};

const DefaultContent: React.FC = () => {
  const { t } = useTranslation();
  return (
    <>
      <Typography align="center" variant="h5" marginBottom={1}>
        <strong>{t('dialog.user_delete.title')}</strong>
      </Typography>
      <Typography align="center" variant="body1" marginBottom={1}>
        {t('dialog.user_delete.text')}
      </Typography>
      <Typography align="center" variant="body2">
        {t('dialog.user_delete.subscription_hint')}
      </Typography>
    </>
  );
};

const DeleteUserAccountDialog: React.FC<DeleteUserAccountDialogProps> = ({
  logout,
  open,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasFailed, setHasFailed] = React.useState(false);

  const handleDeleteAccount = () => {
    setIsLoading(true);
    setHasFailed(false);
    UsersService.usersDelete()
      .then(() => {
        onClose();
        logout();
        window.location.href = `${AUTH_URL}${i18n.language}/register`;
      })
      .catch((err) => {
        console.error(err);
        setHasFailed(true);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog maxWidth="xs" open={open} onClose={onClose}>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {!isLoading && hasFailed ? <FailedContent /> : <DefaultContent />}
      </DialogContent>
      <DialogActions>
        <Button color="error" disabled={isLoading} onClick={handleDeleteAccount}>
          {t('dialog.user_delete.delete_btn')}
        </Button>
        <Button variant="contained" disabled={isLoading} onClick={onClose}>
          {t('dialog.user_delete.cancel_btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withUserContext(DeleteUserAccountDialog);
